import React, { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import { Card, Button, Form, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { ClipLoader } from "react-spinners";
import { FaCamera, FaPaperPlane, FaChevronDown, FaChevronLeft, FaChevronRight } from "react-icons/fa";

// 작성자별 고유 파스텔 아바타 색상 생성기
const getAvatarColor = (name) => {
  const colors = [
    { bg: "#fee2e2", text: "#ef4444" },
    { bg: "#ffedd5", text: "#f97316" },
    { bg: "#fef9c3", text: "#eab308" },
    { bg: "#dcfce7", text: "#22c55e" },
    { bg: "#ecfdf5", text: "#10b981" },
    { bg: "#e0f2fe", text: "#0ea5e9" },
    { bg: "#dbeafe", text: "#3b82f6" },
    { bg: "#e0e7ff", text: "#6366f1" },
    { bg: "#f3e8ff", text: "#a855f7" },
    { bg: "#fae8ff", text: "#d946ef" },
    { bg: "#ffe4e6", text: "#f43f5e" }
  ];
  if (!name) return colors[7];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

// 깨진 이미지 폴백용 컴포넌트
const ImageWithFallback = ({ src, alt, onClick }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="guestbook-image-fallback" title="이미지를 불러올 수 없습니다.">
        <span>⚠️ 이미지 만료됨</span>
      </div>
    );
  }

  return (
    <motion.img
      src={src}
      className="guestbook-post-gallery-img"
      onClick={onClick}
      onError={() => setHasError(true)}
      whileHover={{ scale: 1.04 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      alt={alt}
    />
  );
};

const Guestbook = () => {
  const [entries, setEntries] = useState([]);
  const [visibleEntries, setVisibleEntries] = useState(10);
  const [form, setForm] = useState({ title: "", contents: "", writer: "", boardType: "bbs", boardPassword: "" });
  const [files, setFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]); // 현재 모달의 이미지 리스트
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // 현재 선택된 이미지 인덱스
  const [endpoint] = useState("https://blogapi.wajok.com");
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // 모바일 스와이프 제어용 터치 좌표 상태
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchEndX(0); // 초기화
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (selectedImages.length <= 1) return;
    const swipeThreshold = 30; // 스와이프 인식을 위한 최소 임계값 (30px로 낮추어 민감도 향상)
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) > swipeThreshold && touchEndX !== 0) {
      if (diff > 0) {
        // 왼쪽으로 쓸기 (Swipe Left) -> 다음 이미지
        setCurrentImageIndex((prev) => (prev + 1) % selectedImages.length);
      } else {
        // 오른쪽으로 쓸기 (Swipe Right) -> 이전 이미지
        setCurrentImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length);
      }
    }
  };

  // 키보드 화살표 키로 이미지 슬라이드 감지
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImages.length === 0) return;
      if (e.key === "ArrowLeft") {
        setCurrentImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length);
      } else if (e.key === "ArrowRight") {
        setCurrentImageIndex((prev) => (prev + 1) % selectedImages.length);
      } else if (e.key === "Escape") {
        setSelectedImages([]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImages]);

  useEffect(() => {
    const getGuestbookList = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`${endpoint}/api/board/list`);
        setEntries(response.data.response);
      } catch (error) {
        console.error("Error fetching guestbook:", error);
      }
      setLoading(false);
    };
    getGuestbookList();
  }, [endpoint]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...newFiles]);

    const previews = newFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setPreviewFiles((prev) => [...prev, ...previews]);
  };

  const handleRemoveFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    setPreviewFiles(previewFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!form.writer || !form.contents || !form.boardPassword) {
      alert("모든 항목을 입력해주세요.");
      return;
    }

    let uploadedFiles = [];
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      try {
        const uploadResponse = await axiosInstance.post(`${endpoint}/api/board/fileupload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        uploadedFiles = uploadResponse.data.response;
      } catch (error) {
        console.error("Error uploading files:", error);
        return;
      }
    }

    axiosInstance
      .post(`${endpoint}/api/board/addBoard`, { ...form, uploadList: uploadedFiles })
      .then((response) => {
        setEntries([response.data.response, ...entries]);
        setForm({ title: "", contents: "", writer: "", boardType: "bbs", boardPassword: "" });
        setFiles([]);
        setPreviewFiles([]);
      })
      .catch((error) => console.error("Error posting entry:", error));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
    const previews = droppedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setPreviewFiles((prev) => [...prev, ...previews]);
  };

  return (
    <div className="guestbook-container">
      {/* 스타일 선언 */}
      <style>{`
        .guestbook-container {
          max-width: 760px;
          margin: 2rem auto;
          padding: 0 16px;
        }
        .guestbook-header-banner {
          text-align: center;
          margin-bottom: 2rem;
        }
        .guestbook-header-banner h2 {
          font-size: 2.2rem;
          font-weight: 850;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 6px;
          letter-spacing: -0.5px;
        }
        .guestbook-header-banner p {
          color: #64748b;
          font-size: 0.95rem;
        }
        .guestbook-write-card {
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
          padding: 24px !important;
          margin-bottom: 2.5rem;
          transition: all 0.3s ease;
        }
        .guestbook-write-card:hover {
          border-color: rgba(99, 102, 241, 0.2);
          box-shadow: 0 12px 30px rgba(99, 102, 241, 0.05);
        }
        .guestbook-form-control {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px 14px;
          font-size: 0.92rem;
          background: #f8fafc;
          transition: all 0.2s ease;
        }
        .guestbook-form-control:focus {
          border-color: #6366f1;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        .guestbook-drop-box {
          border: 2px dashed #cbd5e1;
          background: #f8fafc;
          border-radius: 12px;
          padding: 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .guestbook-drop-box:hover {
          border-color: #6366f1;
          background: rgba(99, 102, 241, 0.02);
          color: #6366f1;
        }
        .guestbook-drop-box.dragging {
          border-color: #6366f1;
          background: rgba(99, 102, 241, 0.08);
          color: #6366f1;
        }
        .guestbook-previews-list {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 12px;
          margin-bottom: 12px;
        }
        .guestbook-preview-wrapper {
          position: relative;
          width: 70px;
          height: 70px;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        .guestbook-preview-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .guestbook-preview-remove {
          position: absolute;
          top: 3px;
          right: 3px;
          background: rgba(0, 0, 0, 0.6);
          color: #ffffff;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .guestbook-preview-remove:hover {
          background: #ef4444;
        }
        .guestbook-submit-btn {
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          border: none;
          border-radius: 12px;
          padding: 10px 24px;
          font-weight: 650;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .guestbook-submit-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(99, 102, 241, 0.25);
          background: linear-gradient(135deg, #4f46e5, #4338ca);
        }
        .guestbook-feed {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .guestbook-post-card {
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.015);
          padding: 20px !important;
          transition: all 0.2s ease;
        }
        .guestbook-post-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.03);
        }
        .guestbook-post-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }
        .guestbook-post-author-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .guestbook-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          font-weight: 700;
          font-size: 1.15rem;
          display: flex;
          align-items: center;
          justify-content: center;
          text-transform: uppercase;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }
        .guestbook-author-name {
          font-weight: 750;
          color: #1e293b;
          font-size: 0.98rem;
        }
        .guestbook-post-date {
          font-size: 0.8rem;
          color: #94a3b8;
        }
        .guestbook-post-body {
          text-align: left;
        }
        .guestbook-post-content {
          font-size: 0.95rem;
          line-height: 1.62;
          color: #334155;
          word-break: break-all;
        }
        .guestbook-post-gallery {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 14px;
        }
        .guestbook-post-gallery-img {
          width: 130px;
          height: 130px;
          object-fit: cover;
          border-radius: 12px;
          cursor: pointer;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
        }
        .guestbook-image-fallback {
          width: 130px;
          height: 130px;
          background: #f1f5f9;
          border: 1px dashed #cbd5e1;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-size: 0.72rem;
          font-weight: 700;
          text-align: center;
          padding: 8px;
        }
        .guestbook-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          touch-action: none; /* 모바일 브라우저의 기본 스크롤/바운스 스와이프 방지 */
        }
        .guestbook-modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 85vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          touch-action: none; /* 터치 이벤트 방해 금지 */
          outline: none;
        }
        .guestbook-modal-image-wrapper {
          position: relative;
          display: inline-flex;
        }
        .guestbook-modal-img {
          max-width: 90vw;
          max-height: 80vh;
          border-radius: 16px;
          object-fit: contain;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
        }
        .guestbook-modal-close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(15, 23, 42, 0.6);
          color: #ffffff;
          border: none;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 15;
        }
        .guestbook-modal-close:hover {
          background: #ef4444;
          transform: scale(1.05);
        }
        .guestbook-modal-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(15, 23, 42, 0.6);
          color: #ffffff;
          border: none;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 12;
          user-select: none;
        }
        .guestbook-modal-nav:hover {
          background: #6366f1;
          color: #ffffff;
          transform: translateY(-50%) scale(1.1);
        }
        .guestbook-modal-nav.prev {
          left: 16px;
        }
        .guestbook-modal-nav.next {
          right: 16px;
        }
        .guestbook-modal-indicator {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(15, 23, 42, 0.7);
          color: #ffffff;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          z-index: 12;
        }
        @media (max-width: 576px) {
          .guestbook-modal-nav {
            width: 36px;
            height: 36px;
          }
          .guestbook-modal-nav.prev {
            left: 8px;
          }
          .guestbook-modal-nav.next {
            right: 8px;
          }
        }
        @media (max-width: 576px) {
          .guestbook-header-banner h2 {
            font-size: 1.8rem;
          }
          .guestbook-write-card {
            padding: 16px !important;
          }
          .guestbook-post-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          .guestbook-post-date {
            align-self: flex-end;
            margin-top: -16px;
          }
          .guestbook-post-gallery-img, .guestbook-image-fallback {
            width: calc(50% - 5px);
            height: 100px;
          }
        }
      `}</style>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <ClipLoader color="#ffffff" size={50} />
            <p className="mt-2">로딩중...</p>
          </div>
        </div>
      )}

      {/* 헤더 타이틀 */}
      <div className="guestbook-header-banner">
        <h2>방명록</h2>
        <p>소중한 방문 흔적을 남겨주세요.</p>
      </div>

      {/* 작성 폼 */}
      <Card className="guestbook-write-card">
        <Form>
          <Row className="mb-2">
            <Col xs={12} sm={6} className="mb-2 mb-sm-0">
              <Form.Control
                type="text"
                name="writer"
                className="guestbook-form-control"
                placeholder="작성자명"
                value={form.writer}
                onChange={handleChange}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Control
                type="password"
                name="boardPassword"
                className="guestbook-form-control"
                placeholder="비밀번호 (수정/삭제 시 필요)"
                value={form.boardPassword}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              name="contents"
              className="guestbook-form-control"
              placeholder="내용을 입력하세요"
              value={form.contents}
              onChange={handleChange}
            />
          </Form.Group>

          {/* 썸네일 미리보기 영역 */}
          {previewFiles.length > 0 && (
            <div className="guestbook-previews-list">
              {previewFiles.map((file, index) => (
                <div key={index} className="guestbook-preview-wrapper">
                  <img src={file.preview} alt={`preview-${index}`} className="guestbook-preview-img" />
                  <span className="guestbook-preview-remove" onClick={(e) => { e.preventDefault(); handleRemoveFile(index); }}>✖</span>
                </div>
              ))}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <div
              className={`guestbook-drop-box ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("fileInput").click()}
            >
              <FaCamera /> 사진 올리기 (클릭 또는 드래그)
            </div>
            
            <Button className="guestbook-submit-btn" onClick={handleSubmit}>
              <FaPaperPlane size={12} /> 등록
            </Button>
          </div>

          <input type="file" id="fileInput" multiple hidden onChange={handleFileChange} />
        </Form>
      </Card>

      {/* 피드 목록 */}
      <div className="guestbook-feed">
        {entries.slice(0, visibleEntries).map((entry) => {
          const avatarColor = getAvatarColor(entry.writer);
          return (
            <Card key={entry.id} className="guestbook-post-card">
              <div className="guestbook-post-header">
                <div className="guestbook-post-author-info">
                  <div 
                    className="guestbook-avatar" 
                    style={{ backgroundColor: avatarColor.bg, color: avatarColor.text }}
                  >
                    {entry.writer ? entry.writer.charAt(0) : "?"}
                  </div>
                  <span className="guestbook-author-name">{entry.writer}</span>
                </div>
                <span className="guestbook-post-date">{formatDate(entry.createAt)}</span>
              </div>
              <div className="guestbook-post-body">
                <div className="guestbook-post-content">
                  {entry.contents.split("\n").map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
                {entry.uploadList && entry.uploadList.length > 0 && (
                  <div className="guestbook-post-gallery">
                    {entry.uploadList.map((image, index) => {
                      const allImages = entry.uploadList.map(img => `${endpoint}/uploads/${img}`);
                      return (
                        <ImageWithFallback
                          key={index}
                          src={`${endpoint}/uploads/${image}`}
                          alt={`guestbook-${entry.id}-${index}`}
                          onClick={() => {
                            setSelectedImages(allImages);
                            setCurrentImageIndex(index);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {entries.length > visibleEntries && (
        <Button variant="outline-primary" className="mt-4 w-100 py-2" style={{ borderRadius: '12px' }} onClick={() => setVisibleEntries(visibleEntries + 10)}>
          더보기 <FaChevronDown size={10} className="ms-1" />
        </Button>
      )}

      {/* 모달 이미지 전체 보기 */}
      <AnimatePresence>
        {selectedImages.length > 0 && (
          <motion.div
            className="guestbook-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImages([])}
          >
            <motion.div
              className="guestbook-modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()} // 내부 클릭 시 오버레이 닫힘 방지
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="guestbook-modal-image-wrapper">
                <button className="guestbook-modal-close" onClick={() => setSelectedImages([])}>✖</button>
                
                {/* 이전 이미지 버튼 */}
                {selectedImages.length > 1 && (
                  <button 
                    className="guestbook-modal-nav prev" 
                    onClick={() => setCurrentImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length)}
                  >
                    <FaChevronLeft size={16} />
                  </button>
                )}
                
                <img 
                  src={selectedImages[currentImageIndex]} 
                  className="guestbook-modal-img" 
                  alt="Selected Guestbook" 
                />
                
                {/* 다음 이미지 버튼 */}
                {selectedImages.length > 1 && (
                  <button 
                    className="guestbook-modal-nav next" 
                    onClick={() => setCurrentImageIndex((prev) => (prev + 1) % selectedImages.length)}
                  >
                    <FaChevronRight size={16} />
                  </button>
                )}

                {/* 이미지 카운터 지시자 */}
                {selectedImages.length > 1 && (
                  <div className="guestbook-modal-indicator" data-html2canvas-ignore="true">
                    {currentImageIndex + 1} / {selectedImages.length}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { Guestbook };
